/**
 * Chain monitor — proactive, scheduled verification of the tamper-evident
 * Decision Ledger.
 *
 * The one-click VerifyChainBadge already proves the chain on demand; this
 * module closes the governance gap that integrity was only ever checked when
 * a human thought to click. It is PURE below the type imports:
 *
 *   • `runChainCheck` wraps the shipped {@link verifyChain} walk, stamps the
 *     check time + entry count, and CLASSIFIES the first break — was an
 *     entry edited in place, deleted (broken back-link / index gap),
 *     inserted (spliced), or reordered?
 *   • `chainAlertFromStatus` turns a status into a banner-ready alert and,
 *     when (and only when) the chain is broken, a notifications.ts-shaped
 *     draft (kind 'finding', severity 'high') so the existing bell +
 *     notificationRoute machinery can surface and deep-link it.
 *   • `shouldReverify` is the tiny scheduling predicate: re-check once the
 *     last verification is older than the interval.
 *   • `registerChainMonitor` is the thin wiring helper: given anything that
 *     can schedule a periodic tick (the agent-scheduler loop, a setInterval,
 *     a test harness), it runs an immediate on-load check and then re-checks
 *     whenever `shouldReverify` says the status has gone stale.
 *
 * No store access, no writes, no fabricated data: callers read the sealed
 * rows (subscribeActivity → toLedgerEntries) and hand them in.
 */

import { verifyChain, type LedgerEntry } from './verifyChain';
import type { Notification } from '../crm/notifications';

/** A notifications.ts-shaped draft, ready for the bell / route machinery. */
export type NotificationDraft = Notification;

/** How the first broken link was tampered with. */
export type ChainBreakKind = 'insert' | 'delete' | 'edit' | 'reorder';

/** Outcome of one monitor pass over the ledger chain. */
export type ChainStatus = {
  ok: boolean;
  /** Index of the first broken link (absent when intact). */
  brokenAt?: number;
  /** Best-effort classification of the first break (absent when intact). */
  kind?: ChainBreakKind;
  /** When this check ran (ms epoch) — drives `shouldReverify`. */
  lastVerifiedAt: number;
  /** Number of sealed entries examined. */
  entryCount: number;
};

export type ChainAlert = {
  severity: 'critical' | 'ok';
  message: string;
  /** Present only when the chain is broken. */
  notification?: NotificationDraft;
};

// ---------------------------------------------------------------------------
// Break classification
// ---------------------------------------------------------------------------

/**
 * Classify the first broken link. `verifyChain` reports WHERE the chain broke
 * and the expected/actual hashes; this maps that evidence onto the four
 * tamper stories a buyer asks about:
 *
 *   • seal mismatch (the entry's own hash no longer covers its payload)
 *     → 'edit' — the entry was altered in place;
 *   • index gap (a position is missing from the contiguous 0..n walk)
 *     → 'delete' — a link was removed outright;
 *   • back-link mismatch where the claimed prevHash matches a LATER entry
 *     → 'reorder' — links were shuffled;
 *   • back-link mismatch where the claimed prevHash matches an EARLIER,
 *     non-adjacent entry → 'insert' — something was spliced in between;
 *   • back-link mismatch matching nothing in the chain → 'delete' — the
 *     predecessor it points at is gone.
 */
function classifyBreak(entries: LedgerEntry[], brokenAt: number): ChainBreakKind {
  // Array position of the broken entry (indices may have gaps when tampered).
  const pos = entries.findIndex((e) => e.index === brokenAt);
  const entry = pos >= 0 ? entries[pos] : undefined;
  if (!entry) return 'delete';

  // Index anomaly: the walk expected a different index here.
  const expectedIndex = pos === 0 ? 0 : entries[pos - 1].index + 1;
  if (entry.index !== expectedIndex || entry.index !== pos) {
    return entry.index > pos ? 'delete' : 'reorder';
  }

  // Back-link intact? Then the entry's own seal failed → edited in place.
  const priorHash = pos === 0 ? '' : entries[pos - 1].hash;
  if (entry.prevHash === priorHash) return 'edit';

  // Back-link mismatch: where does the claimed predecessor actually live?
  const claimedPos = entries.findIndex((e) => e.hash === entry.prevHash);
  if (claimedPos > pos) return 'reorder';
  if (claimedPos >= 0 && claimedPos < pos - 1) return 'insert';
  // Points at a hash that exists nowhere in the chain — the link was removed.
  return 'delete';
}

// ---------------------------------------------------------------------------
// Monitor pass
// ---------------------------------------------------------------------------

/**
 * One monitor pass: walk the chain with the SAME verifier the badge uses,
 * stamp when the check ran, and classify the first break (if any). Pure and
 * honest — an empty ledger is intact with entryCount 0.
 */
export function runChainCheck(entries: LedgerEntry[], now: number): ChainStatus {
  const result = verifyChain(entries);
  if (result.ok) {
    return { ok: true, lastVerifiedAt: now, entryCount: result.count };
  }
  const brokenAt = result.brokenAt ?? 0;
  return {
    ok: false,
    brokenAt,
    kind: classifyBreak(entries, brokenAt),
    lastVerifiedAt: now,
    entryCount: result.count,
  };
}

/** Human labels for the break kinds, for the banner + notification copy. */
const KIND_LABEL: Record<ChainBreakKind, string> = {
  edit: 'an entry was edited in place',
  delete: 'an entry was deleted (broken back-link)',
  insert: 'an entry was spliced in',
  reorder: 'entries were reordered',
};

/**
 * Banner/notification derivation. Intact chains get an 'ok' alert with NO
 * notification (nothing needs attention); broken chains get a 'critical'
 * alert plus a notifications.ts-shaped draft (kind 'finding', severity
 * 'high') that the existing bell + notificationRoute machinery can carry.
 */
export function chainAlertFromStatus(status: ChainStatus): ChainAlert {
  const when = new Date(status.lastVerifiedAt).toISOString();
  if (status.ok) {
    return {
      severity: 'ok',
      message: `Ledger chain intact — ${status.entryCount} sealed ${
        status.entryCount === 1 ? 'entry' : 'entries'
      } verified at ${when}.`,
    };
  }
  const kind = status.kind ?? 'edit';
  const message = `Ledger chain integrity FAILED at entry ${status.brokenAt ?? 0}: ${KIND_LABEL[kind]}. Verified at ${when}.`;
  return {
    severity: 'critical',
    message,
    notification: {
      id: `finding:ledger-chain-${status.brokenAt ?? 0}`,
      kind: 'finding',
      severity: 'high',
      title: 'Governance · Ledger chain break detected',
      detail: `Integrity check failed at entry ${status.brokenAt ?? 0} — ${KIND_LABEL[kind]}.`,
      target: `ledger-entry-${status.brokenAt ?? 0}`,
    },
  };
}

/**
 * Scheduling predicate: re-verify once the last check is at least
 * `intervalMs` old. Drives both the on-interval loop and any "is this stale?"
 * badge logic.
 */
export function shouldReverify(status: ChainStatus, now: number, intervalMs: number): boolean {
  return now - status.lastVerifiedAt >= intervalMs;
}

// ---------------------------------------------------------------------------
// Thin wiring: schedule the check like the agent scheduler schedules agents
// ---------------------------------------------------------------------------

/**
 * Anything that can wake us periodically — the agent-scheduler loop, a raw
 * setInterval, or a test harness. Returns an unsubscribe/cancel function.
 */
export interface ChainMonitorScheduler {
  schedule(tick: () => void, intervalMs: number): () => void;
}

export interface ChainMonitorOptions {
  /** Read the sealed chain (subscribeActivity → toLedgerEntries upstream). */
  loadEntries: () => LedgerEntry[] | Promise<LedgerEntry[]>;
  /** Receives every fresh status — banner + notification wiring lives here. */
  onStatus: (status: ChainStatus) => void;
  /** Minimum gap between re-verifications. Default: 5 minutes. */
  intervalMs?: number;
  /** Clock, injectable for tests. */
  now?: () => number;
}

/**
 * Register the monitor: run an immediate on-load check, then re-check on the
 * scheduler's tick whenever {@link shouldReverify} says the last status has
 * gone stale. Returns a cancel function. Thin by design — all decisions are
 * the pure helpers above.
 */
export function registerChainMonitor(
  scheduler: ChainMonitorScheduler,
  opts: ChainMonitorOptions,
): () => void {
  const intervalMs = opts.intervalMs ?? 5 * 60_000;
  const now = opts.now ?? Date.now;
  let last: ChainStatus | null = null;
  let cancelled = false;

  const check = async () => {
    try {
      const entries = await opts.loadEntries();
      if (cancelled) return;
      last = runChainCheck(entries, now());
      opts.onStatus(last);
    } catch {
      /* best-effort — a failed read must not kill the loop */
    }
  };

  // On-load check, immediately.
  void check();

  const unschedule = scheduler.schedule(() => {
    if (cancelled) return;
    if (last === null || shouldReverify(last, now(), intervalMs)) void check();
  }, intervalMs);

  return () => {
    cancelled = true;
    unschedule();
  };
}
